import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useAlert, useApi, useMountAsync, useNewDownloads, useWebSocket } from "hooks";
import env from "environments/env";
import {
  AppContext,
  DownloadsContext,
  FeatureFlagContext,
  UserContext,
  StationContext,
} from "contexts";
import { Route, Switch } from "react-router-dom";
import { Path, Portal, ReportStatus } from "enums";
import { REPORT_UPDATED_EVENT } from "utils";
import AppLoader from "components/commons/app-loader/app-loader";
import { Redirect, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import Header from "components/commons/header/header";
import Sidebar from "components/commons/sidebar/sidebar";
import { getUserV2 } from "apis/profile.api";
import { getStationById } from "apis/station.api";
import RecordsPage from "./records-page";
import BirReportPage from "./bir-report.page";
import VoidReportPage from "./void-report.page";
import CashTrackingPage from "./cash-tracking.page";
import InventoryPage from "./inventory.page";
import DownloadsPage from "./downloads.page";

const AuthorizedPage = () => {
  const { appState } = useContext(AppContext);
  const { setUserEmail } = useContext(FeatureFlagContext);
  const [user, setUser] = useState();
  const [station, setStation] = useState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation(); // add this inside the component
  const { RenderAlerts } = useAlert();

  useWebSocket({ URL: env.WEB_SOCKET_URL });
  const downloads = useNewDownloads({ userId: user?.userId });
  const { completeDownload, discardPendingDownload } = downloads;

  // count a finished export as soon as the websocket reports it
  useEffect(() => {
    const onReportUpdated = ({ detail }) => {
      if (detail?.status === ReportStatus.Success) {
        completeDownload(detail.fileName, detail.reportId);
      } else if (detail?.status === ReportStatus.Failed) {
        discardPendingDownload(detail.fileName);
      }
    };
    window.addEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
    return () => window.removeEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
  }, [completeDownload, discardPendingDownload]);

  const { request: getUserRequest, loading: fetchingUser } = useApi({
    api: getUserV2,
    pageError: true,
  });

  const { request: getStationRequest, loading: fetchingStation } = useApi({
    api: getStationById,
    pageError: true,
  });

  const { request: getStationByStationId } = useApi({
    api: getStationById,
    pageError: false,
    modalError: false,
    handleOwnError: true,
  });

  useMountAsync(() => {
    fetchUser();
    fetchStation();
  });

  const fetchUser = useCallback(async () => {
    const res = await getUserRequest();

    const getStationIds = (res) => {
      const accessRoles = res?.userAccesses
        ?.filter((element) => element.portal === Portal.BOS)
        .map((roleAccess) => (!isNaN(roleAccess?.stationId) ? roleAccess?.stationId : null));

      return accessRoles.filter((stationId) => !!stationId);
    };

    setUser({
      ...res,
    });
    setUserEmail(res.email);

    const stationIds = getStationIds(res);
    if (stationIds?.length > 0) {
      try {
        Promise.all(
          stationIds.map((stationId) => getStationByStationId({ stationId: stationId }))
        ).then((stations) => {
          const userAccesses = res.userAccesses?.map((roleAccess) => ({
            ...roleAccess,
            station: stations?.find(({ stationId }) => stationId === roleAccess?.stationId) || null,
          }));
          setUser((user) => ({
            ...user,
            userAccesses,
          }));
        });
      } catch (error) {}
    }
  }, [getStationByStationId, getUserRequest, setUserEmail]);

  const fetchStation = useCallback(async () => {
    const stationId = localStorage.getItem("stationId");
    if (!stationId || stationId === "undefined" || stationId === "null") {
      localStorage.removeItem("stationId");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = Path.Auth;
      return;
    }
    const station = await getStationRequest({ stationId });
    setStation(station);
  }, [getStationRequest]);

  const loading = fetchingStation || fetchingUser || appState?.loading;
  return (
    <UserContext.Provider value={{ user, fetchUser, loading: fetchingUser }}>
      <StationContext.Provider value={{ station, fetchStation, loading: fetchingStation }}>
        <DownloadsContext.Provider value={downloads}>
        {loading ? (
          <AppLoader />
        ) : user && station ? (
          <Fragment>
            <Header onClick={() => setIsDrawerOpen(!isDrawerOpen)} />
            <Sidebar />

            <div
              style={{
                marginLeft: "196px",
                marginTop: "60px",
                padding: "27px 36px",
                minWidth: "1170px",
                overflowX: "auto",
                minHeight: "100vh",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  overflowX: "auto",
                  padding: "27px 36px",
                }}
              >
                <RenderAlerts />
                <Switch>
                  <Route key={location.key} path={Path.Records} component={RecordsPage} />
                  <Route key={location.key} path={Path.BirReport} component={BirReportPage} />
                  <Route
                    key={location.key}
                    path={Path.VoidTransaction}
                    component={VoidReportPage}
                  />
                  <Route key={location.key} path={Path.CashTracking} component={CashTrackingPage} />
                  <Route key={location.key} path={Path.Inventory} component={InventoryPage} />
                  <Route key={location.key} path={Path.Downloads} component={DownloadsPage} />
                  <Redirect to={Path.ShiftRecords} />
                </Switch>
              </div>
            </div>
          </Fragment>
        ) : null}
        </DownloadsContext.Provider>
      </StationContext.Provider>
    </UserContext.Provider>
  );
};

export default AuthorizedPage;
